/**
 * Renders a reconciliation lifecycle as a horizontal process flow.
 * Used in the Finance agent (Ophelos AI) when the user opens via "Talk to AI" from the briefing.
 */

import { Button } from '@ophelos/ui/components/button';
import { cn } from '@ophelos/ui/lib/utils';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getLifecycleById } from './reconciliationLifecycles';

const contentBoxClass = 'rounded-xl border border-border bg-muted/30 dark:bg-muted/20';

export interface LifecycleFlowProps {
  lifecycleId: string;
  /** When set, highlight this step (e.g. current step for a selected item). */
  currentStepId?: string;
  /** Label for the "Proceed" link (e.g. "Go to Bank statements"). */
  proceedLabel?: string;
  /** When set, show a Proceed button linking to this path. */
  proceedHref?: string;
}

export function LifecycleFlow({ lifecycleId, currentStepId, proceedLabel, proceedHref }: LifecycleFlowProps) {
  const lifecycle = getLifecycleById(lifecycleId);
  if (!lifecycle) return null;

  return (
    <div className={cn(contentBoxClass, 'p-3 mb-4')}>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{lifecycle.label} lifecycle</p>
      <div className="flex flex-wrap items-center gap-1">
        {lifecycle.steps.map((step, idx) => (
          <span key={step.id} className="flex items-center gap-1">
            <span
              className={cn(
                'rounded-md px-2 py-1 text-xs font-medium border',
                step.needs_manual_review ? 'highlight-amber' : 'border-border/50 bg-muted/20 text-muted-foreground',
                currentStepId === step.id && 'ring-2 ring-primary ring-offset-2 ring-offset-card'
              )}
            >
              {step.label}
            </span>
            {idx < lifecycle.steps.length - 1 && <ChevronRight className="size-3.5 text-muted-foreground shrink-0" />}
          </span>
        ))}
      </div>
      <p className="mt-1.5 flex items-center gap-1.5 text-[10px] text-muted-foreground/80">
        <span className="inline-flex items-center gap-1">
          <span className="size-2 rounded-sm border highlight-amber" aria-hidden />
          your action
        </span>
        <span className="text-muted-foreground/50">·</span>
        <span className="inline-flex items-center gap-1">
          <span className="size-2 rounded-sm border border-border bg-muted/40" aria-hidden />
          waiting or done
        </span>
      </p>
      {proceedHref && (
        <div className="mt-3 pt-2 border-t border-border/50">
          <Button variant="default" size="sm" asChild>
            <Link to={proceedHref}>
              {proceedLabel ?? 'Proceed'}
              <ExternalLink className="size-3.5 ml-1 shrink-0" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
